import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackFooter from "@/components/FeedbackFooter";

const EFFECTIVE_DATE = "August 2026";

const Section = ({ title, children }) => (
    <section className="mb-10">
        <h2 className="font-heading text-2xl font-[700] tracking-tight mb-4">{title}</h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">{children}</div>
    </section>
);

export default function Privacy() {
    return (
        <div className="min-h-screen relative overflow-hidden bg-background">
            <div className="blob blob-peach w-[500px] h-[500px] -top-40 -left-40" />
            <div className="blob blob-cream w-[500px] h-[500px] top-1/2 -right-40 opacity-40" />

            <nav className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
                <Link to="/" className="flex items-center gap-2" data-testid="privacy-brand-link">
                    <div className="w-9 h-9 rounded-2xl bg-primary/90 flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-2xl font-[800] tracking-tight">CheerUp!</span>
                </Link>
                <Link to="/" data-testid="privacy-back-link">
                    <Button variant="ghost" className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back home
                    </Button>
                </Link>
            </nav>

            <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 pb-24 pt-6"
                data-testid="privacy-page"
            >
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Privacy Policy</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight mb-4">
                    Your softness is safe here.
                </h1>
                <p className="text-muted-foreground mb-2">Effective {EFFECTIVE_DATE}</p>
                <p className="text-muted-foreground mb-12 leading-relaxed">
                    CheerUp! ("we", "our", "us") is a gentle AI-companion wellness app. This policy explains what
                    we collect, why, and the choices you have. We wrote it in plain language on purpose.
                </p>

                <Section title="1. What we collect">
                    <p>When you use CheerUp!, we collect only what's necessary to give you a caring, useful experience:</p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Account info</strong> — your name, email address, and (for password logins) a securely hashed password. If you use Google sign-in, we also store your Google profile picture.</li>
                        <li><strong>Wellness content you create</strong> — mood check-ins, notes, chat messages with the AI companion, and items you save as favorites.</li>
                        <li><strong>Technical data</strong> — a session cookie or token to keep you signed in, timestamps of activity, and basic error logs for reliability.</li>
                    </ul>
                    <p>We do <strong>not</strong> collect payment information, precise location, contacts, or your browsing outside CheerUp!.</p>
                </Section>

                <Section title="2. How we use your data">
                    <ul className="list-disc pl-6 space-y-2">
                        <li>To provide the CheerUp! service — showing your mood history, remembering past conversations, saving your favorites.</li>
                        <li>To generate empathetic AI responses. Your chat messages are sent to our AI provider (Anthropic Claude) so the assistant can reply.</li>
                        <li>To keep the service secure, prevent abuse, and diagnose issues.</li>
                    </ul>
                    <p>We do <strong>not</strong> sell your data, ever. We do not use your personal wellness content to train foundation AI models.</p>
                </Section>

                <Section title="3. Third-party services we use">
                    <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Anthropic</strong> — powers the AI companion. Messages are transmitted to Anthropic's API for response generation, subject to their privacy terms.</li>
                        <li><strong>Google OAuth</strong> — optional sign-in. If you choose it, Google shares your basic profile (name, email, picture) with us.</li>
                        <li><strong>MongoDB / cloud infrastructure</strong> — where your account and content are securely stored.</li>
                    </ul>
                </Section>

                <Section title="4. How long we keep it">
                    <p>We keep your data for as long as your account is active. When you delete your account, we remove your account record, mood entries, chat history, and favorites within 30 days, except where we are legally required to retain minimal records.</p>
                </Section>

                <Section title="5. Your choices and rights">
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Access, edit, or delete individual mood entries, chat sessions, and favorites from within the app at any time.</li>
                        <li>Request full account deletion by emailing us (see contact below).</li>
                        <li>Depending on where you live, you may have additional rights under GDPR, UK GDPR, CCPA, or similar laws (access, portability, correction, deletion, objection). Contact us and we'll honor them.</li>
                        <li>You can sign out at any time, which clears your session on this device.</li>
                    </ul>
                </Section>

                <Section title="6. Security">
                    <p>Passwords are stored using bcrypt hashing. Data is transmitted over HTTPS. We follow reasonable industry practices to protect your information, but no system is perfectly secure — please use a strong, unique password.</p>
                </Section>

                <Section title="7. Cookies">
                    <p>We use one strictly necessary cookie (or local storage token) to keep you signed in. We do not use advertising cookies or third-party analytics that track you across sites.</p>
                </Section>

                <Section title="8. Children">
                    <p>CheerUp! is intended for adults aged 18 and older. It is not directed to children or teenagers under 18, and we do not knowingly collect personal information from anyone under 18. If you believe a minor has created an account, please contact us and we will remove it.</p>
                    <p><strong>Parent-supervised exception.</strong> In exigent situations, a person under 18 may use CheerUp! only through a <em>parent-supervised account</em>: a parent or legal guardian (18+) creates and owns the account, supervises every session, and is fully responsible for the minor's participation and for the information shared. Any use of CheerUp! by a minor without this direct parental supervision is not permitted. See <Link to="/terms" className="text-primary underline">Terms of Service §1</Link> for full details.</p>
                </Section>

                <Section title="9. Not a crisis service">
                    <p>CheerUp! is a supportive companion, not a substitute for professional mental-health care, therapy, or crisis intervention. If you are in crisis or thinking about harming yourself, please contact a local helpline immediately — in the US call or text <strong>988</strong>, or visit <a href="https://findahelpline.com" target="_blank" rel="noreferrer" className="text-primary underline">findahelpline.com</a> for lines in your region.</p>
                </Section>

                <Section title="10. Changes to this policy">
                    <p>If we make meaningful changes, we'll update the effective date above and, when appropriate, notify you in the app. Continued use after changes means you accept the updated policy.</p>
                </Section>

                <Section title="11. Contact">
                    <p>Questions, requests, or feedback? Reach us at <a href="mailto:query@cheerup-ai.com" className="text-primary underline">query@cheerup-ai.com</a>. We read every message.</p>
                </Section>

                <div className="mt-16 p-8 rounded-3xl bg-accent/40 border border-border">
                    <p className="font-heading text-lg font-[700] mb-2">A little note</p>
                    <p className="text-muted-foreground leading-relaxed">Whatever you carry into CheerUp!, you deserve to feel safe sharing it. We treat your words the way you'd want a kind friend to treat them — gently, and only used to help you feel a little lighter.</p>
                </div>
                <FeedbackFooter />
            </motion.article>
        </div>
    );
}
